// src/components/Auth.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch, setToken, clearToken, fetchMe } from '../api'

/**
 * Auth (Login only)
 *
 * - ForgotPassword.jsx et ResetPassword.jsx gèrent le flow "mot de passe oublié"
 * - Ici: connexion + lien vers /forgot-password
 * - UI claire/fraîche + layout symétrique (même hauteur gauche/droite sur desktop)
 * - KIRASS GROUP = plateforme (toi)
 * - tenantName = nom de la structure client (configurable)
 */

function KirassMark({ className = '' }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10 24L24 10h10L20 24l14 14H24L10 24z"
        fill="currentColor"
        opacity="0.95"
      />
      <path
        d="M22 24L34 12h4L26 24l12 12h-4L22 24z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  )
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path
        d="M2 12s3.5-7 10-7c2.2 0 4.1.7 5.7 1.7M22 12s-3.5 7-10 7c-2.2 0-4.1-.7-5.7-1.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M10.2 10.2A3 3 0 0 0 12 15a3 3 0 0 0 1.8-.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function IconMail(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" {...props}>
      <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M4.5 7l7.5 6 7.5-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  )
}

const REMEMBER_ME_KEY = 'fedis_auth_remember'
const REMEMBERED_USERNAME_KEY = 'fedis_auth_username'

export default function Auth({
  onLoggedIn,

  // nom structure client (configurable)
  tenantName,
  // compat ancien prop
  productName = 'Votre structure',

  // toi = plateforme
  platformBrand = 'KIRASS GROUP',

  madeBy = 'AKGroup UB',
  signatureLabel = 'Conception & intégration',

  // mail admin (toi)
  adminEmail = 'azdinekirasse1@gmail.com',
}) {
  const navigate = useNavigate()

  const [username, setUsername] = useState(() => {
    try {
      return localStorage.getItem(REMEMBERED_USERNAME_KEY) || ''
    } catch {
      return ''
    }
  })
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return localStorage.getItem(REMEMBER_ME_KEY) === '1'
    } catch {
      return false
    }
  })

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [booting, setBooting] = useState(true)
  const [connected, setConnected] = useState(false)
  const [touched, setTouched] = useState({ username: false, password: false })

  const usernameRef = useRef(null)
  const passwordRef = useRef(null)

  const orgName = (tenantName || productName || 'Votre structure').trim()
  const year = new Date().getFullYear()

  const userInitials = useMemo(() => {
    const u = (username || '').trim()
    if (!u) return 'U'
    return u
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0]?.toUpperCase())
      .join('')
  }, [username])

  const userHint = useMemo(() => {
    const u = (username || '').trim()
    return u ? `Identifiant: ${u}` : 'Saisis tes identifiants'
  }, [username])

  const usernameError = touched.username && !username.trim() ? 'Identifiant requis.' : ''
  const passwordError = touched.password && !password.trim() ? 'Mot de passe requis.' : ''
  const hasInlineError = Boolean(usernameError || passwordError)

  const adminMailto = useMemo(() => {
    const subject = `[${orgName}] Problème de connexion`
    const body =
      `Bonjour,\n\n` +
      `Je n’arrive pas à me connecter à l’espace: ${orgName}\n\n` +
      `Identifiant: ${username || '(non renseigné)'}\n` +
      `Problème: \n\n` +
      `Merci.\n`
    return `mailto:${adminEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }, [adminEmail, orgName, username])

  function goToHomeByRole(roles = []) {
    const r = Array.isArray(roles) ? roles : []
    if (r.includes('admin')) return navigate('/dashboard', { replace: true })
    if (r.includes('ops')) return navigate('/products', { replace: true })
    if (r.includes('sales')) return navigate('/sales', { replace: true })
    return navigate('/orders', { replace: true })
  }

  // Si déjà connecté, récupérer profil et rediriger
  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const me = await fetchMe()
        if (!mounted) return
        setConnected(true)
        try { localStorage.setItem('user', JSON.stringify(me || null)) } catch {}
        goToHomeByRole(me?.roles || [])
      } catch {
        if (!mounted) return
        setConnected(false)
      } finally {
        if (mounted) setBooting(false)
      }
    })()
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // focus à l'arrivée
  useEffect(() => {
    if (connected) return
    setTimeout(() => usernameRef.current?.focus(), 80)
  }, [connected])

  function mapAuthError(err) {
    const code = err?.data?.error || err?.body?.error || err?.error || err?.message || ''
    const status = err?.status || err?.code || null
    const s = String(code).toLowerCase()

    if (s.includes('too_many_attempts') || status === 429) return 'Trop de tentatives. Reessaie dans quelques minutes.'
    if (s.includes('account_disabled')) return 'Compte desactive. Contacte l administrateur.'
    if (s.includes('email_not_verified')) return 'Email non verifie. Consulte ta boite mail ou demande un nouveau lien sur /verify-email.'
    if (s.includes('unauthorized') || s.includes('invalid')) return 'Identifiants invalides.'
    return 'Identifiants incorrects ou probleme de connexion.'
  }

  async function handleLogin(e) {
    e.preventDefault()
    if (loading) return

    setTouched({ username: true, password: true })
    setError('')

    if (!username.trim() || !password.trim()) {
      setError('Complète les champs requis.')
      if (!username.trim()) setTimeout(() => usernameRef.current?.focus(), 60)
      else setTimeout(() => passwordRef.current?.focus(), 60)
      return
    }

    setLoading(true)
    try {
      const data = await apiFetch('/api/auth/login', {
        method: 'POST',
        body: { username, password },
      })

      if (data?.token) setToken(data.token)
      setConnected(true)

      try {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_ME_KEY, '1')
          localStorage.setItem(REMEMBERED_USERNAME_KEY, username.trim())
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY)
          localStorage.removeItem(REMEMBERED_USERNAME_KEY)
        }
      } catch {}

      try { localStorage.setItem('user', JSON.stringify(data.user || null)) } catch {}
      onLoggedIn?.({ token: data?.token || '', user: data.user })

      goToHomeByRole(data?.user?.roles || [])
    } catch (err) {
      console.error('Erreur de connexion:', err)
      setError(mapAuthError(err))
      clearToken()
      setConnected(false)
      setTimeout(() => passwordRef.current?.focus(), 60)
    } finally {
      setLoading(false)
    }
  }

  function handleLogout() {
    clearToken()
    try { localStorage.removeItem('user') } catch {}
    setConnected(false)
    onLoggedIn?.()
    if (!rememberMe) setUsername('')
    setPassword('')
    setError('')
    setTouched({ username: false, password: false })
    setTimeout(() => usernameRef.current?.focus(), 80)
  }

  // UI helpers
  const CardShell =
    'h-full rounded-[28px] border border-slate-200 bg-white/75 shadow-[0_25px_90px_-60px_rgba(15,23,42,.20)] backdrop-blur-xl'

  return (
    <div className="min-h-screen text-slate-900">
      {/* Background clair/frais */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
        <div className="absolute -top-40 left-[-10%] h-[520px] w-[520px] rounded-full bg-sky-300/50 blur-3xl" />
        <div className="absolute top-10 right-[-15%] h-[560px] w-[560px] rounded-full bg-slate-300/40 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[35%] h-[520px] w-[520px] rounded-full bg-emerald-300/35 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.18] bg-[linear-gradient(to_right,rgba(15,23,42,.09)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,.09)_1px,transparent_1px)] bg-[size:56px_56px]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-stretch px-4 py-10">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8 items-stretch">
          {/* LEFT (desktop only) */}
          <div className={`relative hidden lg:flex lg:col-span-6 ${CardShell} p-8 flex-col justify-between min-h-[560px]`}>
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                  <KirassMark className="h-7 w-7 text-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-[11px] tracking-[0.22em] text-slate-500">{platformBrand}</div>
                  <div className="text-lg font-semibold text-slate-900">{orgName}</div>
                </div>
              </div>

              <div className="mt-12 max-w-sm">
                <div className="text-3xl font-semibold tracking-tight text-slate-900">Connexion</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Connecte-toi pour accéder à ton espace. Si tu as oublié ton mot de passe, utilise le lien de récupération.
                  En cas de blocage, contacte l’admin.
                </p>

                {/* Un seul lien contact */}
                <a
                  href={adminMailto}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-[0_18px_60px_-40px_rgba(15,23,42,.35)] transition hover:bg-slate-800"
                  title="Ouvrir la messagerie (mail pré-rempli)"
                >
                  <IconMail className="opacity-90" />
                  Contacter l’admin
                </a>
              </div>
            </div>

            <div>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                  <div className="text-xs text-slate-500">Sécurité</div>
                  <div className="mt-1 text-sm font-medium text-slate-900">Accès contrôlé</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
                  <div className="text-xs text-slate-500">Assistance</div>
                  <div className="mt-1 text-sm font-medium text-slate-900">Récupération + support</div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                <span>© {year} {orgName}</span>
                <span className="tracking-[0.18em] text-slate-400">Propulsé par {platformBrand}</span>
              </div>

              <div className="pointer-events-none absolute bottom-6 left-6 flex items-center gap-2 opacity-[0.06]">
                <KirassMark className="h-5 w-5 text-slate-900" />
                <span className="text-xs tracking-[0.24em] text-slate-900">{platformBrand}</span>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex h-full items-stretch justify-center lg:col-span-6">
            <div className="w-full max-w-lg h-full flex flex-col">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 shadow-sm">
                  <KirassMark className="h-4 w-4 text-slate-900" />
                  <span className="text-[11px] tracking-[0.22em] text-slate-600">{platformBrand}</span>
                </div>
                <div className="text-xs text-slate-500">© {year}</div>
              </div>

              <div className={`${CardShell} min-h-[560px] flex flex-col overflow-hidden`}>
                <div className="px-7 pt-7">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white font-semibold shadow-sm">
                      {userInitials}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-500">
                        {orgName}
                      </div>
                      <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                        Connexion
                      </div>
                      <div className="mt-1 text-sm text-slate-600 truncate">
                        {connected ? 'Session active' : userHint}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-7 pb-7 pt-6 flex-1">
                  {booting ? (
                    <div className="space-y-4" aria-live="polite" aria-busy="true">
                      <div className="h-5 w-2/3 animate-pulse rounded bg-slate-200" />
                      <div className="h-12 w-full animate-pulse rounded-2xl bg-slate-100" />
                      <div className="h-12 w-full animate-pulse rounded-2xl bg-slate-100" />
                      <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200" />
                    </div>
                  ) : connected ? (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-600 text-white font-bold">
                            ✓
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-emerald-900">Connecté</div>
                            <div className="text-sm text-emerald-800 mt-0.5">Redirection en cours…</div>
                            <div className="text-xs text-emerald-700 mt-2">Pour changer de compte, déconnecte-toi.</div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={handleLogout}
                        className="w-full rounded-2xl px-4 py-3 font-medium border border-slate-200 bg-white/70 text-slate-900 hover:bg-white transition-colors"
                      >
                        Se déconnecter
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      {error && (
                        <div
                          className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                          role="alert"
                          aria-live="assertive"
                        >
                          {error}
                        </div>
                      )}

                      <div>
                        <label htmlFor="auth-username" className="text-sm font-medium text-slate-800">Identifiant</label>
                        <div className="mt-1.5">
                          <input
                            id="auth-username"
                            ref={usernameRef}
                            value={username}
                            onChange={(e) => {
                              setUsername(e.target.value)
                              if (error) setError('')
                              if (touched.username) {
                                setTouched((prev) => ({ ...prev, username: true }))
                              }
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, username: true }))}
                            disabled={loading}
                            placeholder="admin / email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            aria-invalid={Boolean(usernameError)}
                            aria-describedby={usernameError ? 'auth-username-error' : undefined}
                            className={`w-full rounded-2xl border bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400
                                       outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-slate-200/60
                                       ${usernameError ? 'border-red-300 focus:border-red-300 focus:ring-red-100' : 'border-slate-200'}`}
                          />
                        </div>
                        {usernameError && (
                          <p id="auth-username-error" className="mt-1.5 text-xs font-medium text-red-700" aria-live="polite">
                            {usernameError}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="auth-password" className="text-sm font-medium text-slate-800">Mot de passe</label>
                        <div className="mt-1.5 flex gap-2">
                          <input
                            id="auth-password"
                            ref={passwordRef}
                            type={showPw ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value)
                              if (error) setError('')
                              if (touched.password) {
                                setTouched((prev) => ({ ...prev, password: true }))
                              }
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                            disabled={loading}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            aria-invalid={Boolean(passwordError)}
                            aria-describedby={passwordError ? 'auth-password-error' : undefined}
                            className={`w-full rounded-2xl border bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400
                                       outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-slate-200/60
                                       ${passwordError ? 'border-red-300 focus:border-red-300 focus:ring-red-100' : 'border-slate-200'}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPw(v => !v)}
                            disabled={loading}
                            className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 text-slate-700 hover:bg-slate-50 transition disabled:opacity-60"
                            title={showPw ? 'Masquer' : 'Afficher'}
                            aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          >
                            <EyeIcon open={showPw} />
                          </button>
                        </div>
                        {passwordError && (
                          <p id="auth-password-error" className="mt-1.5 text-xs font-medium text-red-700" aria-live="polite">
                            {passwordError}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            disabled={loading}
                            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                          />
                          Se souvenir de moi
                        </label>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => navigate('/forgot-password')}
                            className="text-sm font-medium text-slate-800 hover:text-slate-900"
                          >
                            Mot de passe oublié ?
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate('/signup')}
                            className="text-sm font-medium text-slate-700 hover:text-slate-900"
                          >
                            Créer un compte
                          </button>
                        </div>

                        <button
                          type="submit"
                          disabled={loading || !username.trim() || !password.trim() || hasInlineError}
                          className={`inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-white font-medium transition
                            ${loading || !username.trim() || !password.trim() || hasInlineError
                              ? 'bg-slate-300 cursor-not-allowed'
                              : 'bg-slate-900 hover:bg-slate-950 shadow-[0_18px_60px_-40px_rgba(79,70,229,.55)]'
                            }`}
                        >
                          {loading ? 'Vérification…' : 'Se connecter'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                <div className="px-7 pb-7">
                  <div className="pt-2 space-y-2">
                    <div className="text-[11px] text-center text-slate-500 leading-relaxed">
                      Astuce: ne partage jamais tes identifiants. Si l’accès échoue plusieurs fois, attends quelques minutes avant de réessayer.
                    </div>
                    <div className="text-[11px] text-center text-slate-500">
                      <span>{signatureLabel}</span>{' '}
                      <span className="text-slate-300">•</span>{' '}
                      <span className="font-medium text-slate-700">{madeBy}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile-only “contact admin” */}
              <div className="mt-6 lg:hidden">
                <a
                  href={adminMailto}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm font-medium text-slate-900 hover:bg-white transition"
                >
                  <IconMail />
                  Contacter l’admin
                </a>
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-12">
            <div className="mt-2 text-center text-xs text-slate-500">
              <span className="opacity-80">Propulsé par</span>{' '}
              <span className="tracking-[0.22em]">{platformBrand}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
